import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { navLinks } from "@/mocks/site";

export default function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/events?q=${encodeURIComponent(q)}` : "/events");
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-background-50/95 border-b border-background-200 backdrop-blur"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="h-16 md:h-[72px] flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5 group shrink-0">
            <span className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center font-heading text-lg font-bold">
              瓜
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-lg md:text-xl font-bold text-foreground-950 tracking-tight">
                瓜链
              </span>
              <span className="hidden sm:block text-[10px] md:text-[11px] text-foreground-500 mt-0.5 tracking-wide">
                网红黑曝传播追踪
              </span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1" aria-label="主导航">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                className={({ isActive }) =>
                  `px-4 py-2 rounded-md text-sm font-label transition-colors whitespace-nowrap ${
                    isActive
                      ? "text-primary-700 bg-primary-50"
                      : "text-foreground-700 hover:text-foreground-950 hover:bg-background-100"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2 md:gap-3">
            <form
              onSubmit={submitSearch}
              className="hidden xl:flex items-center gap-2 bg-background-100 border border-background-200 rounded-md px-3 h-10 w-56 focus-within:border-primary-300 transition-colors"
              role="search"
            >
              <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                <i className="ri-search-line text-base" />
              </span>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索事件或关键词"
                className="flex-1 bg-transparent outline-none text-sm text-foreground-900 placeholder:text-foreground-400"
                aria-label="搜索事件"
              />
            </form>

            <Link
              to="/#subscribe"
              className="hidden md:inline-flex items-center gap-1.5 h-10 px-4 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-label whitespace-nowrap transition-colors cursor-pointer"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-notification-3-line" />
              </span>
              订阅简报
            </Link>

            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className="lg:hidden w-10 h-10 flex items-center justify-center rounded-md border border-background-200 text-foreground-800 hover:bg-background-100 cursor-pointer"
              aria-label={menuOpen ? "关闭菜单" : "打开菜单"}
              aria-expanded={menuOpen}
            >
              <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
            </button>
          </div>
        </div>
      </div>

      <div
        className={`lg:hidden overflow-hidden transition-all duration-300 bg-background-50 border-b border-background-200 ${
          menuOpen ? "max-h-[420px]" : "max-h-0 border-b-transparent"
        }`}
      >
        <div className="px-4 md:px-8 py-4 flex flex-col gap-1">
          {navLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.to === "/"}
              className={({ isActive }) =>
                `px-4 py-3 rounded-md text-sm font-label whitespace-nowrap ${
                  isActive ? "text-primary-700 bg-primary-50" : "text-foreground-700 hover:bg-background-100"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
          <form
            onSubmit={submitSearch}
            className="flex items-center gap-2 bg-background-100 border border-background-200 rounded-md px-3 h-11 mt-2"
            role="search"
          >
            <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
              <i className="ri-search-line text-base" />
            </span>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索事件或关键词"
              className="flex-1 bg-transparent outline-none text-sm text-foreground-900 placeholder:text-foreground-400"
              aria-label="搜索事件"
            />
          </form>
          <Link
            to="/#subscribe"
            className="mt-2 h-11 flex items-center justify-center rounded-md bg-primary-500 text-background-50 text-sm font-label whitespace-nowrap"
          >
            订阅追踪简报
          </Link>
        </div>
      </div>
    </header>
  );
}